import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { serverErrorMessage } from '../../hooks/useSubscriptionLines';
import { LINE_BASES } from '../../lineTypes';
import type { LineBasis, SubscriptionLine, SubscriptionLineInput, SubscriptionLineUpdate } from '../../lineTypes';

interface LineFormDialogProps {
  open: boolean;
  /** The line being edited, or null to add one. */
  line: SubscriptionLine | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SubscriptionLineInput | SubscriptionLineUpdate) => Promise<void>;
}

interface FormState {
  sku: string;
  description: string;
  quantity: string;
  unitPrice: string;
  discount: string;
  basis: LineBasis;
}

const EMPTY: FormState = { sku: '', description: '', quantity: '1', unitPrice: '', discount: '0', basis: 'fixed' };

function fromLine(line: SubscriptionLine | null): FormState {
  if (!line) return EMPTY;
  return {
    sku: line.sku,
    description: line.description ?? '',
    quantity: String(Number(line.quantity)),
    unitPrice: String(Number(line.unitPrice)),
    discount: String(Number(line.discount)),
    basis: line.basis,
  };
}

/** Mirrors the server's 400 rules: sku required, quantity and unit price non-negative numbers. */
function toInput(form: FormState): SubscriptionLineInput | null {
  const quantity = Number(form.quantity);
  const unitPrice = Number(form.unitPrice);
  const discount = Number(form.discount || 0);
  if (!form.sku.trim() || form.quantity === '' || form.unitPrice === '') return null;
  if (![quantity, unitPrice, discount].every(Number.isFinite)) return null;
  if (quantity < 0 || unitPrice < 0 || discount < 0) return null;
  return {
    sku: form.sku.trim(),
    description: form.description.trim() || null,
    quantity,
    unitPrice,
    discount,
    basis: form.basis,
  };
}

/** An edit sends only what changed, so the server never sees an untouched field (D10 resets verification). */
function changedFields(next: SubscriptionLineInput, prev: SubscriptionLineInput | null): SubscriptionLineUpdate {
  if (!prev) return next;
  const out: SubscriptionLineUpdate = {};
  for (const key of Object.keys(next) as Array<keyof SubscriptionLineInput>) {
    if (next[key] !== prev[key]) (out as Record<string, unknown>)[key] = next[key];
  }
  return out;
}

export function LineFormDialog({ open, line, isSubmitting, onOpenChange, onSubmit }: LineFormDialogProps) {
  const { t } = useTranslation('subscriptions');
  const [form, setForm] = useState<FormState>(fromLine(line));
  const [invalid, setInvalid] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(fromLine(line));
      setInvalid(false);
      setServerError(null);
    }
  }, [open, line]);

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    const input = toInput(form);
    if (!input) {
      setInvalid(true);
      return;
    }
    setServerError(null);
    try {
      await onSubmit(line ? changedFields(input, toInput(fromLine(line))) : input);
    } catch (e) {
      setServerError(serverErrorMessage(e) || t('lines.saveFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{line ? t('lines.editTitle') : t('lines.addTitle')}</DialogTitle>
        </DialogHeader>

        {line && (
          <Alert data-testid="line-edit-resets-notice">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('lines.editResetsVerification')}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label>{t('lines.sku')}</Label>
            <Input data-testid="line-sku" value={form.sku} onChange={(e) => set({ sku: e.target.value })} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('lines.basis')}</Label>
            <Select
              value={form.basis}
              // Radix fires onValueChange('') when the value is not mounted yet; ignore it.
              onValueChange={(v) => { if (v) set({ basis: v as LineBasis }); }}
            >
              <SelectTrigger data-testid="line-basis-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LINE_BASES.map((b) => (
                  <SelectItem key={b} value={b}>{t(`lines.bases.${b}`, b)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5 col-span-2">
            <Label>{t('lines.description')}</Label>
            <Input data-testid="line-description" value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('lines.quantity')}</Label>
            <Input data-testid="line-quantity" type="number" min={0} step="0.01" value={form.quantity}
              onChange={(e) => set({ quantity: e.target.value })} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('lines.unitPrice')}</Label>
            <Input data-testid="line-unit-price" type="number" min={0} step="0.01" value={form.unitPrice}
              onChange={(e) => set({ unitPrice: e.target.value })} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('lines.discount')}</Label>
            <Input data-testid="line-discount" type="number" min={0} step="0.01" value={form.discount}
              onChange={(e) => set({ discount: e.target.value })} />
          </div>
        </div>

        {invalid && (
          <p data-testid="line-form-error" className="text-xs text-destructive">{t('lines.invalid')}</p>
        )}
        {serverError && (
          <p data-testid="line-server-error" className="text-xs text-destructive">{serverError}</p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common:cancel', 'Cancel')}</Button>
          <Button type="button" data-testid="line-save" disabled={isSubmitting} onClick={handleSave}>
            {t('lines.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
